import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ChannelsIcon,
    ProspectsIcon,
    HomeIcon,
    EhrIcon,
    KlarityReviewsIcon,
    CalendarIcon,
    InboxIcon,
    FileTextOutlined,
    TextVisitIcon,
    PrescriptionsIcon,
    FinancialsIcon,
    EFaxIcon,
    PracticeOverviewIcon,
    CredentialingIcon,
    InsuranceBillingIcon,
} from 'components/MenuIcons';
import SubscriptionStore from 'store/Subscription';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Spin, Button, Alert, Popover, Tour, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { MenuProps, TourProps } from 'antd';
import s from './s.module.less';
import { PATH } from 'constants/path';
import ProviderStore from 'store/Provider';
import { EProviderType, Provider, HomeInfo, EFeatureType } from 'types/provider';
import { trackHeapEvent } from 'utils/analytics';
import TreatmentTemplatesIcon from './imgs/treatmentTemplate.svg';
import Logo from 'assets/logo/klarity.png';
//import Logo from 'assets/logo/logo-2412.png';

//import Logo from 'assets/campain/blackfriday2024/kiwiLogo.png';
// import LogoMini from 'assets/campain/blackfriday2024/kiwiLogo_mini.png';
import PolicyIcon from './imgs/policy.svg';
import HeartIcon from './imgs/heart.svg';
import MedicationIcon from './imgs/medication.svg';
import SettingIcon from './imgs/setting.svg';
import LogoutIcon from './imgs/logout.svg';
import useLoadBasicData from 'hooks/useLoadBasicData';
import {
    openSubscriptionModal,
    openManageSubscriptionModal,
    openKlaritySubscriptionModal,
} from 'utils/globalLayerControl';
import { redirecToPPALogout } from 'utils/redirecToLogout';
import {
    EDot,
    EKey,
    getHasViewDotPage,
    getHasViewKiwiEhrPage,
    getHasViewKlarityReviewPage,
    getKey,
    setHasViewDotPage,
    setHasViewKiwiEhrPage,
    setHasViewKlarityReviewPage,
    getClickedMeetingLinkSettingSiderMenu,
    setClickedMeetingLinkSettingSiderMenu,
} from 'utils/localstore';
import { authUserWithSwitchKey, ERegisterType } from 'api/public';
import { processAuthResponse } from 'utils/auth';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';
import { isFreeUser, isPaidFailUser, shouldShowKlarityPlanUI } from 'utils/provider';
import useGetMyChannels from 'hooks/useGetMyChannels';
import ProviderChannelsStore from 'store/ProviderChannles';
import useGetUniprofileStatus from 'hooks/useGetUniprofileStatus';
import useProviderServiceFlag from 'hooks/useProviderServiceFlag';
import ProviderAgreement from 'components/ProviderAgreement';
import { TSubscriptionType } from 'types/subscription';
import useGetKlarityReviewList from 'hooks/useGetKlarityReviewList';
import { EOpenType, ESortType } from 'pages/dashboard/KlarityReviewsDashboradPage/types';
import ReviewStore from 'store/Review';
import AlertStore from 'store/GlobalAlert';
import { getViewMode, setHelpCenterActiveKey } from 'utils/sessionStorage';
import {
    isKlarityHost,
    isLocalhost,
    canShowGroupPracticeView,
    canShowTeamManagementView,
    canShowPracticeGroupFuns,
    canShowPracticeSwitcher,
    canShowPracticeOverview,
} from 'utils/common';
import useInboxAllUnreadCount from 'hooks/inbox/useInboxAllUnreadCount';
import useBudgetInfo from 'hooks/marketing/useBudgetInfo';
import ProfilePopover from 'components/ProfilePopover';
import useCheckLeadFormByEmail from 'hooks/ppa/useCheckLeadFormByEmail';
import { isCredentialingHelpType, hasIntent, EHelpType } from 'pages/PPALeadFormPage/helpType';
import { EPPAStep } from 'types/ppaOnboarding';
import { EProfileStatus } from 'types/channel';
import { markTextVisitNotificationAsRead } from 'api/textVisit';
import AddAndUpdateProspects from 'components/AddAndUpdateProspects';
import AddAndEditCalendarEventModal from 'components/AddAndEditCalendarEvent/Modal';
import CreateVisitNoteModal, {
    CreateVisitNoteResult,
} from 'pages/dashboard/ProspectsDetailPage/components/treatmentPlan/components/CreateVisitNoteModal';
import VideoVisitUnSubmittedDrawer from 'components/VideoVisitUnSubmittedDrawer';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import VideoVisitSubmittedDrawer from 'components/VideoVisitSubmittedDrawer';
import NonAppointmentUnSubmittedVisitNoteDrawer from 'components/NonAppointmentUnSubmittedVisitNoteDrawer';
import { KlarityService } from 'types/common';
import useKlarityServiceTemplate from 'hooks/useKlarityServiceTemplate';
import useCurrentUser from 'hooks/useCurrentUser';
import { getDisplayInfo } from 'utils/group';
import PracticeSwitcher from 'components/PracticeSwitcher';
import { MODE, MENU_PERMISSION_CONFIG, isDevEnv } from 'constants/common';
import { ERoleCode } from 'types/enumItems';
import useHomePopup from 'hooks/useHomePopup';
import { EHomePopupKey } from 'constants/homePopups';
import useSupportPracticeList from 'hooks/useSupportPracticeList';
import useVideoSetting from 'hooks/useVideoSetting';
import { getCredentialingOverview } from 'api/credentialing';

const { Content, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    ref?: React.RefObject<HTMLDivElement>,
): MenuItem {
    return {
        key,
        icon: ref ? undefined : icon, // 当有 ref 时，不设置 icon 属性
        children,
        label: ref ? (
            <div ref={ref} style={{ display: 'flex', alignItems: 'center' }}>
                {icon}
                <span style={{ marginLeft: icon ? '8px' : '0' }}>{label}</span>
            </div>
        ) : (
            label
        ),
    } as MenuItem;
}

enum MenuKey {
    HOME = 'home',
    APPOINTMENT = 'appointment',
    CHANNELS = 'channels',
    PROSPECTS = 'prospects',
    CLIENTS = 'clients',
    PRACTICE_FRONT = 'practice-front',
    EHR = 'ehr',
    REPUTATION_MANAGEMENT = 'reputation-management',
    KLARITY_REVIEWS = 'klarity-reviews',
    KLARITY_ASSESSMENT = 'klarity-assessment',
    CALENDAR = 'calendar',
    INBOX = 'inbox',
    MARKETING = 'marketing',
    PROFILE = 'profile',
    TEXT_VISIT = 'text-visit',
    PRESCRIPTIONS = 'prescriptions',
    FINANCIALS = 'financials',
    EFAX = 'efax',
    PRACTICE_OVERVIEW = 'practice-overview',
    INSURANCE_BILLING = 'insurance-billing',
    CREDENTIALING = 'credentialing',
}

const keyPathMap: Partial<Record<MenuKey, string>> = {
    [MenuKey.EHR]: `${PATH.DASHBOARD}/${PATH.EHR_HOME_PROVIDER}`,
    [MenuKey.HOME]: `${PATH.DASHBOARD}/${PATH.PROVIDER_HOME}`,
    [MenuKey.CHANNELS]: `${PATH.DASHBOARD}/${PATH.CHANNEL}`,
    [MenuKey.APPOINTMENT]: `${PATH.DASHBOARD}/${PATH.APPOINTMENT}`,
    [MenuKey.PRACTICE_FRONT]: `${PATH.DASHBOARD}/${PATH.PRACTICE_FRONT}`,
    [MenuKey.PROSPECTS]: `${PATH.DASHBOARD}/${PATH.PROSPECTS}`,
    [MenuKey.CLIENTS]: `${PATH.DASHBOARD}/${PATH.CLIENTS}`,
    [MenuKey.REPUTATION_MANAGEMENT]: `${PATH.DASHBOARD}/${PATH.REPUTATION_MANAGEMENT}`,
    [MenuKey.KLARITY_REVIEWS]: `${PATH.DASHBOARD}/${PATH.KLARITY_REVIEWS}`,
    [MenuKey.CALENDAR]: `${PATH.DASHBOARD}/${PATH.CALENDAR}`,
    [MenuKey.INBOX]: `${PATH.DASHBOARD}/${PATH.INBOX}`,
    [MenuKey.MARKETING]: `${PATH.DASHBOARD}/${PATH.MARKETING}`,
    [MenuKey.PROFILE]: `${PATH.DASHBOARD}/${PATH.PROFILE}`,
    [MenuKey.TEXT_VISIT]: `${PATH.DASHBOARD}/${PATH.TEXT_VISIT}`,
    [MenuKey.PRESCRIPTIONS]: `${PATH.DASHBOARD}/${PATH.PRESCRIPTIONS}`,
    [MenuKey.FINANCIALS]: `${PATH.DASHBOARD}/${PATH.FINANCIALS}`,
    [MenuKey.EFAX]: `${PATH.DASHBOARD}/${PATH.EFAX}`,
    [MenuKey.PRACTICE_OVERVIEW]: `${PATH.DASHBOARD}/${PATH.PRACTICE_OVERVIEW}`,
    [MenuKey.INSURANCE_BILLING]: `${PATH.DASHBOARD}/${PATH.INSURANCE_BILLING}`,
    [MenuKey.CREDENTIALING]: `${PATH.DASHBOARD}/${PATH.CREDENTIALING}`,
};

const getDefaultKey = (pathStr?: string): MenuKey => {
    const path = pathStr || window.location.pathname;
    if (path.includes(MenuKey.APPOINTMENT)) {
        //setting also use this
        return MenuKey.APPOINTMENT;
    }
    if (path.includes('channel')) {
        return MenuKey.CHANNELS;
    }
    if (path.includes('ehr')) {
        return MenuKey.EHR;
    }
    if (path.includes('practice-front')) {
        return MenuKey.PRACTICE_FRONT;
    }
    if (path.includes('clients') || path.includes('prospects')) {
        return MenuKey.CLIENTS;
    }
    if (path.includes('clients') || path.includes('prospects')) {
        return MenuKey.CLIENTS;
    }
    if (path.includes('reputation')) {
        return MenuKey.REPUTATION_MANAGEMENT;
    }
    if (path.includes('klarity-reviews')) {
        return MenuKey.KLARITY_REVIEWS;
    }
    if (path.includes('calendar')) {
        return MenuKey.CALENDAR;
    }
    if (path.includes('inbox') || path.includes('ava-setting')) {
        return MenuKey.INBOX;
    }
    if (path.includes('marketing')) {
        return MenuKey.MARKETING;
    }
    if (path.includes('profile')) {
        return MenuKey.PROFILE;
    }
    if (path.includes('text-visit')) {
        return MenuKey.TEXT_VISIT;
    }
    if (path.includes('prescriptions')) {
        return MenuKey.PRESCRIPTIONS;
    }
    if (path.includes('financials')) {
        return MenuKey.FINANCIALS;
    }
    if (path.toLowerCase().includes('efax')) {
        return MenuKey.EFAX;
    }
    if (path.includes('practice-overview')) {
        return MenuKey.PRACTICE_OVERVIEW;
    }
    if (path.includes('insurance-billing')) {
        return MenuKey.INSURANCE_BILLING;
    }
    if (path.includes('credentialing')) {
        return MenuKey.CREDENTIALING;
    }
    return MenuKey.HOME;
};

interface PPALayoutProps {
    homeInfo?: HomeInfo;
}

const PPALayout = ({ homeInfo }: PPALayoutProps) => {
    const [getGloalAlert, setGlobalAlert] = AlertStore.useStore();
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(getKey(EKey.SIDEBAR_COLLAPSED)) === 'true';
        } catch {
            return false;
        }
    });
    const [get, set] = ProviderStore.useStore();
    const [currentKey, setCurrentKey] = useState<MenuKey>(getDefaultKey());
    const provider: Provider = get('data') as Provider;
    const toggleCollapsed = useCallback(() => {
        trackHeapEvent('WebApp - PPA Layout - Click - Toggle Sidebar', {
            collapsed,
        });
        const next = !collapsed;
        setCollapsed(next);
        try {
            localStorage.setItem(getKey(EKey.SIDEBAR_COLLAPSED), String(next));
        } catch { /* */ }
    }, [collapsed]);

    // const [down]

    const navigate = useNavigate();
    const location = useLocation();
    const [loadSettingData] = useLoadBasicData();
    const [, loadingSubscriptionPlan] = useSubscriptionPlan();

    const [getSubscription] = SubscriptionStore.useStore();
    const currentPlan = getSubscription('currentPlan');

    const isKlarityStarter = currentPlan?.type === TSubscriptionType.KLARITY_STARTER;
    const isFree = isFreeUser();
    const isPaidFail = isPaidFailUser();
    const [myChannels, loadingMychannels] = useGetMyChannels();
    const [, setProviderChannels] = ProviderChannelsStore.useStore();
    const [isOpenUpgrade, setIsOpenUpgrade] = useState(false);
    const [allUnreadCount, , refetchAllUnreadCount] = useInboxAllUnreadCount();
    const [, adBudgetStatus] = useBudgetInfo();
    const [leadFormData, leadFormLoading] = useCheckLeadFormByEmail(provider?.email || '');

    const [flag] = useProviderServiceFlag();
    const isKlarity = !!flag?.klarityServiceFlag;
    const kiwiEhrHighLightValue = !getHasViewKiwiEhrPage();
    const prospectHighLightValue = !getHasViewDotPage(EDot.PROSPECT_0810);
    const marketingHighLightValue =
        !getHasViewDotPage(EDot.MARKETING_0903) ||
        adBudgetStatus.isBudgetNearlyExhausted ||
        adBudgetStatus.isCostAllBudget;
    // 检查本地存储和接口返回的 notificationTypes 字段
    const hasNewTextVisitNotification = homeInfo?.notificationTypes?.includes('NEW_TEXT_VISIT') ?? false;
    const textVisitHighLightValue = !getHasViewDotPage(EDot.TEXT_VISIT_0923) || hasNewTextVisitNotification;
    const prescriptionsHighLightValue = !getHasViewDotPage(EDot.PRESCRIPTIONS_1021);
    const efaxHighLightValue = !getHasViewDotPage(EDot.EFAX_041126);
    const insuranceBillingHighLightValue = !getHasViewDotPage(EDot.INSURANCE_BILLING_0817);
    const [videoSettingData] = useVideoSetting();
    const calendarHighLightValue =
        !getHasViewDotPage(EDot.CHARTING_PREFERENCE_1105) ||
        (videoSettingData?.meetingLinkGenerationType === 'CUSTOM' && !getClickedMeetingLinkSettingSiderMenu());
    const showFinancialsTagValue = !getHasViewDotPage(EDot.FINANCIALS_TAG_1128);
    const [showTeamManagementModal, setShowTeamManagementModal] = useState(false);
    const [showPracticeSwitcher, setShowPracticeSwitcher] = useState(false);

    const [calendarHighLight, setCalendarHighLight] = useState(false);
    const [prospectHighLight, setProspectHighLight] = useState(false);
    const [marketingHighLight, setMarketingHighLight] = useState(false);
    const [kiwiEhrHighLight, setKiwiEhrHighLight] = useState(false);
    const [textVisitHighLight, setTextVisitHighLight] = useState(false);
    const [prescriptionsHighLight, setPrescriptionsHighLight] = useState(false);
    const [efaxHighLight, setEfaxHighLight] = useState(false);
    const [insuranceBillingHighLight, setInsuranceBillingHighLight] = useState(false);
    const [financialsHighLight, setFinancialsHighLight] = useState(false);
    const [showFinancialsTag, setShowFinancialsTag] = useState(false);
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
    const [showCreateVisitNoteModal, setShowCreateVisitNoteModal] = useState(false);
    const [visitNoteAppointmentId, setVisitNoteAppointmentId] = useState<number>();
    const [visitNoteTextVisitUuid, setVisitNoteTextVisitUuid] = useState<string>();
    const [showVisitNoteVideoDrawer, setShowVisitNoteVideoDrawer] = useState(false);
    const [showVisitNoteTextVisitUnSubmittedDrawer, setShowVisitNoteTextVisitUnSubmittedDrawer] = useState(false);
    const [showVisitNotePreviewDrawer, setShowVisitNotePreviewDrawer] = useState(false);
    const [showVisitNoteViewTextDrawer, setShowVisitNoteViewTextDrawer] = useState(false);
    const [showNonAppointmentUnSubmittedVisitNoteDrawer, setShowNonAppointmentUnSubmittedVisitNoteDrawer] =
        useState(false);
    const [nonAppointmentSpecialty, setNonAppointmentSpecialty] = useState<string | undefined>();
    const [nonAppointmentClientId, setNonAppointmentClientId] = useState<number | undefined>();
    const [nonAppointmentNoteId, setNonAppointmentNoteId] = useState<number | undefined>();
    const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
    const [allSpeciality, setAllSpeciality] = React.useState<KlarityService[]>([]);
    const [template, loadingTemplate, refetchTemplate] = useKlarityServiceTemplate();
    const [currentUser, loadingCurrentUser] = useCurrentUser();
    const [practiceList, loadingPracticeList, refetchPracticeList] = useSupportPracticeList();
    const showAdminLabel = currentUser?.isAdmin && currentUser?.groupPractice;

    const roleCode = currentUser?.roleCode as ERoleCode | undefined;
    const isGroupAdmin = currentUser?.isAdmin;
    const showTeamManagement = canShowTeamManagementView(currentUser);

    React.useEffect(() => {
        if (template) {
            const allData: KlarityService[] = [...(template?.recommend || []), ...(template?.other || [])];
            setAllSpeciality(allData);
        }
        //todo remove
        //openManageSubscriptionModal('9');
    }, [template]);

    useEffect(() => {
        setCalendarHighLight(calendarHighLightValue);
        setProspectHighLight(prospectHighLightValue);
        setMarketingHighLight(marketingHighLightValue);
        setKiwiEhrHighLight(kiwiEhrHighLightValue);
        setTextVisitHighLight(textVisitHighLightValue);
        setPrescriptionsHighLight(prescriptionsHighLightValue);
        setEfaxHighLight(efaxHighLightValue);
        setInsuranceBillingHighLight(insuranceBillingHighLightValue);
        setShowFinancialsTag(showFinancialsTagValue);
    }, [
        calendarHighLightValue,
        prospectHighLightValue,
        marketingHighLightValue,
        kiwiEhrHighLightValue,
        textVisitHighLightValue,
        prescriptionsHighLightValue,
        efaxHighLightValue,
        insuranceBillingHighLightValue,
        showFinancialsTagValue,
        homeInfo,
    ]);

    const isShowSwitchAccount = homeInfo?.providerTypes?.length === 2;
    const isPPAProvider = !!homeInfo?.ppaProvider;
    const showTextVisit = !!homeInfo?.features?.includes(EFeatureType.TEXT_VISIT);
    const isOpsPreview = getViewMode() === MODE.OPS_PREVIEW;
    const homeRef = useRef(null);
    const messageRef = useRef(null);
    const calendarRef = useRef(null);
    const clientRef = useRef(null);
    const klarityReviewRef = useRef(null);
    const marketRef = useRef(null);
    const prescriptionsRef = useRef(null);
    const financeRef = useRef(null);
    const profileRef = useRef(null);
    const textVisitRef = useRef(null);
    const dropRef = useRef(null);
    const [open, setOpen] = useState<boolean>(false);
    const { shouldShow: shouldShowKlarityTour } = useHomePopup(EHomePopupKey.KLARITY_HOME_TOUR, open);
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(0);

    // 如果klarityUser === true, 那么就肯定是ppv
    // 如果ppaProvider === true, 那么就肯定是ppa
    // 剩下的就是既不是既不是ppa也不是ppv，（kiwi user）
    const isPPVProvider = !!homeInfo?.ppvProvider;

    const defautlReviewListParams = useRef({
        sortType: ESortType.MOST_RECENT,
        publicOrPrivate: EOpenType.BOTH_PUBLIC_AND_PRIVATE,
        size: 10,
        current: 1,
        waitToReply: true,
    });
    const [, , loadingReviewList] = useGetKlarityReviewList(defautlReviewListParams.current);
    const [getReviewStore] = ReviewStore.useStore();
    const klarityReviewWaitToReplyNumber = getReviewStore('klarityReviewWaitToReplyNumber');

    const klarityReviewHighLight =
        !getHasViewKlarityReviewPage() || (klarityReviewWaitToReplyNumber && klarityReviewWaitToReplyNumber > 0);

    const showKlarityPlanUI = shouldShowKlarityPlanUI(homeInfo);

    // Use case 1:the provider is Kiwi only; not listed on Klarity --> Kiwi Reputation
    // Use case 2:Klarity with Kiwi for Klarity; not paying for Kiwi before --> Klarity Review
    // Use case 3:Klarity & pay for Kiwi currently --> Let’s keep both tab
    const showKiwiRepucation = false;
    const showInbox = true;
    const showInboxUnreadCount = !!(allUnreadCount && allUnreadCount > 0);
    const hasCredentialing = !leadFormLoading && isCredentialingHelpType(leadFormData?.leadForm?.practiceHelpType);
    const showCredentialing = true;

    //https://www.figma.com/design/aGY2cI5da2oGElG6GEd0Wo/Group-Practice?node-id=4067-13884&m=dev
    const steps: TourProps['steps'] = [
        {
            title: ' ',
            description:
                'Track your performance and discover growth opportunities — manage everything in one place from your homepage.',
            target: () => homeRef.current,
            placement: 'right',
        },
        {
            title: ' ',
            placement: 'right',
            description:
                'Chat with your patients who have booked with you — answer questions, follow up, and stay connected.',
            target: () => messageRef.current,
        },
        {
            title: ' ',
            placement: 'right',
            description:
                'Manage your appointments and keep your schedule organized. Easily update your availability and sync with your calendar.',
            target: () => calendarRef.current,
        },
        {
            title: ' ',
            placement: 'right',
            description:
                'View detailed profiles of all your patients — from appointment history to important notes — so you’re always prepared.',
            target: () => clientRef.current,
        },
        {
            title: ' ',
            placement: 'right',
            description:
                'Monitor your practice’s reputation — from patient ratings to written feedback — so you can build trust and attract more new patients.',
            target: () => klarityReviewRef.current,
        },
        {
            title: ' ',
            placement: 'right',
            description:
                'Set your bid, monitor results, and view how your practice ranks on Klarity through Pro-Listings.',
            target: () => marketRef.current,
        },
        // {
        //     title: ' ',
        //     placement: 'right',
        //     description:
        //         'Offer flexible care options — by conducting secure, asynchronous text consultations — so you can handle follow-ups on your own schedule.',
        //     target: () => textVisitRef.current,
        // },
        {
            title: ' ',
            placement: 'right',
            description:
                'Streamline medication management — from sending e-prescriptions to authorizing refills — so your patients get the care they need without delay.',
            target: () => prescriptionsRef.current,
        },
        // {
        //     title: ' ',
        //     placement: 'right',
        //     description:
        //         'Streamline medication management — from sending e-prescriptions to authorizing refills — so your patients get the care they need without delay.',
        //     target: () => financeRef.current,
        // },
        {
            title: ' ',
            placement: 'right',
            description: 'Easily check how your profile appears to patients on Klarity.',
            target: () => profileRef.current,
        },
        {
            title: ' ',
            placement: 'right',
            description:
                'Manage everything about your practice here — no-show policies, service templates, visit notes templates, medication settings, and account details. Access your personal account and manage payment methods under Settings anytime.',
            target: () => dropRef.current,
            className: 'tour-step-last',
        },
    ];

    // Handle switch account code parameter
    React.useEffect(() => {
        const handleSwitchAccountCode = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');

            if (code) {
                try {
                    // 调用接口进行认证
                    const authResult = await authUserWithSwitchKey(code);

                    if (authResult.error) {
                        // eslint-disable-next-line no-console
                        console.error('Failed to authenticate with switch key:', authResult.error);
                        return;
                    }

                    // 处理认证响应
                    const loginResult = processAuthResponse(
                        authResult,
                        set,
                        isKlarityHost() ? ERegisterType.KLARITY_PORTAL : ERegisterType.KIWI_PORTAL,
                    );

                    if (loginResult) {
                        // 清除URL中的code参数
                        const newUrl = new URL(window.location.href);
                        newUrl.searchParams.delete('code');
                        window.history.replaceState({}, '', newUrl.toString());

                        // 刷新页面以应用新的认证状态
                        window.location.reload();
                    } else {
                        // eslint-disable-next-line no-console
                        console.error('Failed to process auth response');
                    }
                } catch (switchError) {
                    // eslint-disable-next-line no-console
                    console.error('Error handling switch account code:', switchError);
                }
            }
        };

        handleSwitchAccountCode();
    }, [set]);

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const settingsTab = urlParams.get('settingsTab');
        if (settingsTab) {
            openManageSubscriptionModal(settingsTab);
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('settingsTab');
            window.history.replaceState({}, '', newUrl.toString());
        }
    }, []);

    const items: MenuItem[] = [
        ...(canShowPracticeOverview(currentUser)
            ? [getItem('Practice overview', MenuKey.PRACTICE_OVERVIEW, PracticeOverviewIcon)]
            : []),
        getItem('Home', MenuKey.HOME, HomeIcon, undefined, homeRef),
        getItem(
            showInboxUnreadCount ? (
                <div className={s.itemWithCount}>
                    Messages
                    <span className={s.unread}>{allUnreadCount > 99 ? '99+' : allUnreadCount}</span>
                </div>
            ) : (
                <span>Messages</span>
            ),
            MenuKey.INBOX,
            InboxIcon,
            undefined,
            messageRef,
        ),
        getItem(
            <span className={calendarHighLight ? s.hightLight : ''}>Calendar</span>,
            MenuKey.CALENDAR,
            CalendarIcon,
            undefined,
            calendarRef,
        ),
        isPPVProvider
            ? getItem(<span className={kiwiEhrHighLight ? s.hightLight : ''}>Kiwi EHR</span>, MenuKey.EHR, EhrIcon)
            : null,
        getItem(
            <span className={prospectHighLight ? s.hightLight : ''}>Clients</span>,
            MenuKey.CLIENTS,
            ProspectsIcon,
            undefined,
            clientRef,
        ),
        getItem(
            <span className={klarityReviewHighLight ? s.hightLight : ''}>Klarity reviews</span>,
            MenuKey.KLARITY_REVIEWS,
            KlarityReviewsIcon,
            undefined,
            klarityReviewRef,
        ),
        getItem(
            <span className={marketingHighLight ? s.hightLight : ''}>Marketing</span>,
            MenuKey.MARKETING,
            ChannelsIcon,
            undefined,
            marketRef,
        ),
        getItem(
            <span className={`${textVisitHighLight ? s.hightLight : ''} flex items-center`}>
                Text Visit
                <span className={s.betaTag}>BETA</span>
            </span>,
            MenuKey.TEXT_VISIT,
            TextVisitIcon,
        ),
        // showTextVisit || isPPVProvider || isLocalhost()
        //     ? getItem(
        //           <span className={`${textVisitHighLight ? s.hightLight : ''} flex items-center`}>
        //               Text Visit <span className={s.betaTag}>BETA</span>
        //           </span>,
        //           MenuKey.TEXT_VISIT,
        //           TextVisitIcon,
        //       )
        //     : undefined,
        getItem(
            <span className={prescriptionsHighLight ? s.hightLight : ''}>Prescriptions</span>,
            MenuKey.PRESCRIPTIONS,
            PrescriptionsIcon,
            undefined,
            prescriptionsRef,
        ),
        getItem(<span className={efaxHighLight ? s.hightLight : ''}>eFax</span>, MenuKey.EFAX, EFaxIcon),
        getItem(
            <span className={financialsHighLight ? s.hightLight : ''}>
                Financials{showFinancialsTag ? <span className={s.financeTag}>$10K available</span> : ''}
            </span>,
            MenuKey.FINANCIALS,
            FinancialsIcon,
            undefined,
            financeRef,
        ),
        getItem(
            <span className={`${insuranceBillingHighLight ? s.hightLight : ''} flex items-center`}>
                Insurance billing <span className={s.betaTag}>BETA</span>
            </span>,
            MenuKey.INSURANCE_BILLING,
            InsuranceBillingIcon,
        ),
        showCredentialing ? getItem('Credentialing', MenuKey.CREDENTIALING, CredentialingIcon) : undefined,
        getItem(<span>Profile</span>, MenuKey.PROFILE, FileTextOutlined, undefined, profileRef),
    ]
        .filter(Boolean)
        .filter((item: MenuItem) => {
            if (!roleCode || !item) return true;
            const { key } = item as { key?: string };
            // console.log({
            //     key,
            // });
            if (!key) return true;
            const allowedRoles = MENU_PERMISSION_CONFIG[key];

            if (!allowedRoles) return true;
            const effectiveRole = isGroupAdmin ? ERoleCode.GROUP_PRACTICE_MANAGER : roleCode;
            // console.log({
            //     key,
            //     MENU_PERMISSION_CONFIG,
            //     roleCode,
            //     allowedRoles,
            //     effectiveRole,
            //     canSee: allowedRoles.includes(effectiveRole),
            // });
            return allowedRoles.includes(effectiveRole);
        }) as MenuItem[];

    // if (isLocalhost()) {
    //     items = [
    //         getItem('Home', MenuKey.HOME, HomeIcon, undefined, homeRef),
    //         getItem(
    //             showInboxUnreadCount ? (
    //                 <div className={s.itemWithCount}>
    //                     Messages
    //                     <span className={s.unread}>{allUnreadCount > 99 ? '99+' : allUnreadCount}</span>
    //                 </div>
    //             ) : (
    //                 <span>Messages</span>
    //             ),
    //             MenuKey.INBOX,
    //             InboxIcon,
    //             undefined,
    //             messageRef,
    //         ),
    //         getItem('Calendar', MenuKey.CALENDAR, CalendarIcon, undefined, calendarRef),
    //         isPPVProvider
    //             ? getItem(<span className={kiwiEhrHighLight ? s.hightLight : ''}>Kiwi EHR</span>, MenuKey.EHR, EhrIcon)
    //             : null,
    //         getItem(
    //             <span className={prospectHighLight ? s.hightLight : ''}>Clients</span>,
    //             MenuKey.CLIENTS,
    //             ProspectsIcon,
    //             undefined,
    //             clientRef,
    //         ),
    //         getItem(
    //             <span className={klarityReviewHighLight ? s.hightLight : ''}>Klarity reviews</span>,
    //             MenuKey.KLARITY_REVIEWS,
    //             KlarityReviewsIcon,
    //         ),
    //         getItem(
    //             <span className={marketingHighLight ? s.hightLight : ''}>Marketing</span>,
    //             MenuKey.MARKETING,
    //             ChannelsIcon,
    //             undefined,
    //             klarityReviewRef,
    //         ),
    //         getItem(
    //             <span className={financialsHighLight ? s.hightLight : ''}>
    //                 Financials{showFinancialsTag ? <span className={s.financeTag}>$10K available</span> : ''}
    //             </span>,
    //             MenuKey.FINANCIALS,
    //             FinancialsIcon,
    //         ),
    //         getItem(
    //             <span className={`${textVisitHighLight ? s.hightLight : ''} flex items-center`}>
    //                 Text Visit <span className={s.betaTag}>BETA</span>
    //             </span>,
    //             MenuKey.TEXT_VISIT,
    //             TextVisitIcon,
    //         ),
    //         getItem(
    //             <span className={prescriptionsHighLight ? s.hightLight : ''}>Prescriptions</span>,
    //             MenuKey.PRESCRIPTIONS,
    //             PrescriptionsIcon,
    //         ),
    //         getItem(<span>Profile</span>, MenuKey.PROFILE, FileTextOutlined, undefined, marketRef),
    //     ].filter(Boolean) as MenuItem[];
    // }

    const [, loading] = useGetUniprofileStatus();

    const ProviderMenus = useMemo(
        () =>
            [
                isPPAProvider
                    ? {
                          key: '1',
                          label: (
                              <div
                                  className={s.menuItem}
                                  onClick={() => {
                                      trackHeapEvent('WebApp - PPA Layout - Click - Practice & No-show Policy', {
                                          fromMenu: true,
                                      });
                                      setDropdownOpen(false);
                                      navigate(`/${PATH.PAYMENT_SETUP}?from=menu`);
                                  }}
                              >
                                  <img src={PolicyIcon} alt="Policy" width="20" height="20" />
                                  <span style={{ marginLeft: '8px' }}>Practice & No-show policy</span>
                              </div>
                          ),
                      }
                    : null,
                {
                    key: '2',
                    label: (
                        <div
                            className={s.menuItem}
                            onClick={() => {
                                trackHeapEvent('WebApp - PPA Layout - Click - Service Templates', {
                                    fromMenu: true,
                                    isPPVProvider,
                                });
                                setDropdownOpen(false);
                                if (isPPVProvider) {
                                    navigate(
                                        `/${PATH.SERVICE_SETTING}?forceShowTable=true&providerType=${EProviderType.KLARITY}`,
                                    );
                                } else {
                                    navigate(`/${PATH.PPA_SERVICE_TEMPLATE}?from=menu`);
                                }
                                // TODO: 实现Service templates功能
                                // console.log('Service templates clicked');
                            }}
                        >
                            <img src={HeartIcon} alt="Service templates" width="20" height="20" />
                            <span style={{ marginLeft: '8px' }}>Service templates</span>
                        </div>
                    ),
                },
                {
                    key: '4',
                    label: (
                        <div
                            className={s.menuItem}
                            onClick={() => {
                                trackHeapEvent('WebApp - PPA Layout - Click - Visit Notes templates', {
                                    fromMenu: true,
                                });
                                setDropdownOpen(false);
                                navigate(`${PATH.CALENDAR_SETTING}?from=menu&tab=2&sub=templates`);
                            }}
                        >
                            <img src={TreatmentTemplatesIcon} alt="Visit Notes templates" width="20" height="20" />
                            <span style={{ marginLeft: '8px' }}>Visit Notes templates</span>
                        </div>
                    ),
                },
                {
                    key: '5',
                    label: (
                        <div
                            className={s.menuItem}
                            onClick={() => {
                                trackHeapEvent('WebApp - PPA Layout - Click - Medication Settings', {
                                    fromMenu: true,
                                });
                                setDropdownOpen(false);
                                navigate(`${PATH.MEDICATION_SETTINGS}?from=menu`);
                            }}
                        >
                            <img src={MedicationIcon} alt="Medication settings" width="20" height="20" />
                            <span style={{ marginLeft: '8px' }}>Medication settings</span>
                        </div>
                    ),
                },
                // {
                //     key: '6',
                //     label: (
                //         <div
                //             className={s.menuItem}
                //             onClick={() => {
                //                 setDropdownOpen(false);
                //                 openManageSubscriptionModal('9');
                //             }}
                //         >
                //             <img src={TeamManagementIcon} alt="Team management" width="20" height="20" />
                //             <span style={{ marginLeft: '8px' }}>Team management</span>
                //         </div>
                //     ),
                // },
                canShowPracticeSwitcher(practiceList) && canShowGroupPracticeView(currentUser) && !isOpsPreview
                    ? {
                          key: '6-switch',
                          label: (
                              <div
                                  className={s.menuItem}
                                  onClick={() => {
                                      setDropdownOpen(false);
                                      setShowPracticeSwitcher(true);
                                  }}
                              >
                                  <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      viewBox="0 0 20 20"
                                      fill="none"
                                  >
                                      <path
                                          d="M2.5 6.66667H14.1667M14.1667 6.66667L10.8333 3.33333M14.1667 6.66667L10.8333 10M17.5 13.3333H5.83333M5.83333 13.3333L9.16667 10M5.83333 13.3333L9.16667 16.6667"
                                          stroke="#374151"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                      />
                                  </svg>
                                  <span style={{ marginLeft: '8px' }}>Switch Account</span>
                              </div>
                          ),
                      }
                    : null,
                {
                    key: '7',
                    label: (
                        <div
                            className={s.menuItem}
                            onClick={() => {
                                trackHeapEvent('WebApp - PPA Layout - Click - Settings', {
                                    fromMenu: true,
                                });
                                setHasViewDotPage(EDot.SETTINGS_1121, 'true');
                                setDropdownOpen(false);
                                openManageSubscriptionModal();
                            }}
                        >
                            <img src={SettingIcon} alt="Setting" width="20" height="20" />
                            <span style={{ marginLeft: '8px' }}>
                                Setting
                                {!getHasViewDotPage(EDot.SETTINGS_1121) ? <span className={s.dot} /> : ''}
                            </span>
                        </div>
                    ),
                },
                {
                    key: '8',
                    label: (
                        <div
                            className={s.menuItem}
                            onClick={() => {
                                trackHeapEvent('WebApp - PPA Layout - Click - Logout', {
                                    fromMenu: true,
                                });
                                setDropdownOpen(false);
                                const key = getKey(EKey.BOX_PRACTICE_TOKEN);
                                localStorage.removeItem(key);

                                redirecToPPALogout();
                            }}
                        >
                            <img src={LogoutIcon} alt="Logout" width="20" height="20" />
                            <span style={{ marginLeft: '8px' }}>Logout</span>
                        </div>
                    ),
                },
            ].filter(Boolean),
        [isPPAProvider, navigate, isPPVProvider, practiceList, isOpsPreview],
    );

    useEffect(() => {
        loadSettingData();
        setProviderChannels('data', myChannels);
    }, [loadSettingData, myChannels]);

    const isStaffRole =
        roleCode === ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT ||
        roleCode === ERoleCode.GROUP_CLINICAL_ASSISTANT ||
        roleCode === ERoleCode.GROUP_PRACTICE_MANAGER;

    // Handle PPA onboarding step navigation
    useEffect(() => {
        if (!homeInfo) {
            return;
        }
        if (leadFormLoading || !leadFormData) {
            return;
        }
        // if (isOpsPreview) {
        //     return;
        // }
        if (isStaffRole) {
            return;
        }
        if (
            isPPAProvider &&
            (homeInfo.uniprofileStatus === EProfileStatus.APPROVED ||
                homeInfo.uniprofileStatus === EProfileStatus.UNDER_REVIEW)
        ) {
            //https://klarity-hq.slack.com/archives/C045PJ79L10/p1758564158638949
            //已经opt-in ppa的不需要任何额外信息填写
            return;
        }
        if (isPPVProvider) {
            return;
        }

        const currentPath = window.location.pathname;

        const practiceHelpType = leadFormData?.leadForm?.practiceHelpType;
        const hasPatientGrowth = hasIntent(practiceHelpType, EHelpType.PATIENT_GROWTH);

        // 没有选 patient-growth 的用户不需要走 ppa-onboarding，跳过强制跳转逻辑。
        // 登录时已由 getIntentRedirect 按优先级分流到各自的 landing / dashboard 页面。
        if (practiceHelpType && !hasPatientGrowth && !leadFormLoading) {
            return;
        }

        if (currentPath.includes('billing')) {
            return;
        }

        // 1.不是历史数据，而是没走过onboarding 流程, 2. 是ppv并没有走完onboarding 流程
        if (
            !leadFormData?.listedOnKlarity &&
            !leadFormData?.onBoardingStep &&
            !leadFormLoading &&
            !homeInfo?.klarityUser &&
            !currentPath.includes('ppa-onboarding') &&
            !currentPath.includes('ppa-klarity-reviews') &&
            !currentPath.includes('credentialing')
        ) {
            // Credentialing 用户不进入常规 ppa-onboarding，而是根据是否完成 landing/onboarding 决定去向：
            //   - 已完成（存在 provider onboardingStatus === COMPLETED）→ dashboard credentialing
            //   - 未完成 → credentialing landing page
            if (isCredentialingHelpType(leadFormData?.leadForm?.practiceHelpType)) {
                getCredentialingOverview()
                    .then((res: any) => {
                        const providers = res?.data?.data?.providers || [];
                        const completedLanding = providers.some(
                            (p: { onboardingStatus?: string }) => p.onboardingStatus === 'COMPLETED',
                        );
                        navigate(
                            completedLanding ? `${PATH.DASHBOARD}/${PATH.CREDENTIALING}` : PATH.CREDENTIALING_LANDING,
                            { replace: true },
                        );
                    })
                    .catch(() => navigate(PATH.CREDENTIALING_LANDING, { replace: true }));
                return;
            }
            //https://applink.larksuite.com/client/todo/detail?guid=bea31b67-93e1-4beb-9006-80b572a0225d&suite_entity_num=t103735
            //navigate(PATH.PPA_ONBOARDING_STEPS, { replace: true });
            return;
        }
        //https://applink.larksuite.com/client/todo/detail?guid=bea31b67-93e1-4beb-9006-80b572a0225d&suite_entity_num=t103735
        return;

        if (leadFormData?.onBoardingStep && !leadFormLoading) {
            const onboardingStep = leadFormData.onBoardingStep;

            // Only redirect if not already on the correct page or ppa-klarity-reviews page
            if (
                !currentPath.includes('ppa-onboarding') &&
                !currentPath.includes('ppa-klarity-reviews') &&
                !currentPath.includes('credentialing') &&
                !currentPath.includes('billing')
            ) {
                switch (onboardingStep) {
                    case EPPAStep.STEP_1:
                    case EPPAStep.STEP_2:
                    case EPPAStep.STEP_3:
                    case EPPAStep.STEP_4:
                    case EPPAStep.STEP_5:
                    case EPPAStep.STEP_6:
                    case EPPAStep.STEP_7:
                    case EPPAStep.STEP_8:
                    case EPPAStep.STEP_9:
                    case EPPAStep.STEP_10:
                    case EPPAStep.STEP_11:
                    case EPPAStep.STEP_12:
                    case EPPAStep.STEP_13:
                    case EPPAStep.STEP_14:
                    case EPPAStep.STEP_15:
                    case EPPAStep.STEP_16:
                    case EPPAStep.STEP_17:
                        navigate(PATH.PPA_ONBOARDING_STEPS, { replace: true });
                        break;
                    case EPPAStep.STEP_17_1:
                        navigate(PATH.PPA_ONBOARDING_REVIEW, { replace: true });
                        break;
                    case EPPAStep.STEP_18:
                        navigate(PATH.PPA_ONBOARDING_FINISH, { replace: true });
                        break;
                    case EPPAStep.STEP_19:
                    case EPPAStep.STEP_20:
                        navigate(PATH.PPA_ONBOARDING_ADVANCE_SETTING, { replace: true });
                        break;
                    default:
                        // If step is not recognized, stay on current page
                        break;
                }
            }
        }
    }, [
        leadFormData,
        leadFormLoading,
        navigate,
        isPPAProvider,
        homeInfo,
        isPPVProvider,
        isStaffRole,
        hasCredentialing,
    ]);

    const onClickMenu = async ({ key }: { key: string }) => {
        const path: string | undefined = keyPathMap[key as MenuKey];

        trackHeapEvent('WebApp - PPA Layout - Click - Menu Item', {
            menuKey: key,
            path,
            currentKey,
        });

        if (key === MenuKey.CALENDAR) {
            setClickedMeetingLinkSettingSiderMenu(true);
            setHasViewDotPage(EDot.CHARTING_PREFERENCE_1105, 'true');
            setCalendarHighLight(false);
        }
        if (key === MenuKey.EHR) {
            setHasViewKiwiEhrPage('true');
            setKiwiEhrHighLight(false);
        }
        if (key === MenuKey.PROSPECTS || key === MenuKey.CHANNELS || key === MenuKey.CLIENTS) {
            setHasViewDotPage(EDot.PROSPECT_0810, 'true');
            setProspectHighLight(false);
        }
        if (key === MenuKey.MARKETING) {
            setHasViewDotPage(EDot.MARKETING_0903, 'true');
            setMarketingHighLight(false);
        }
        if (key === MenuKey.TEXT_VISIT) {
            setHasViewDotPage(EDot.TEXT_VISIT_0923, 'true');
            setTextVisitHighLight(false);
            // 如果 textVisitHighLightValue 为 true，调用接口标记通知已读
            if (textVisitHighLightValue) {
                try {
                    await markTextVisitNotificationAsRead();
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to mark text visit notification as read:', error);
                }
            }
        }
        if (key === MenuKey.PRESCRIPTIONS) {
            setHasViewDotPage(EDot.PRESCRIPTIONS_1021, 'true');
            setPrescriptionsHighLight(false);
        }
        if (key === MenuKey.EFAX) {
            setHasViewDotPage(EDot.EFAX_041126, 'true');
            setEfaxHighLight(false);
        }
        if (key === MenuKey.INSURANCE_BILLING) {
            setHasViewDotPage(EDot.INSURANCE_BILLING_0817, 'true');
            setInsuranceBillingHighLight(false);
        }
        if (key === MenuKey.KLARITY_REVIEWS) {
            setHasViewKlarityReviewPage('true');
        }
        if (path) {
            navigate(path);
            setCurrentKey(key as MenuKey);
        }
        refetchAllUnreadCount();
    };

    useEffect(() => {
        const newKey = getDefaultKey(location.pathname);
        setCurrentKey(newKey);
        document.getElementById('sitePPALayout')?.scrollTo(0, 0);
    }, [location]);

    useEffect(() => {
        if (homeInfo && !localStorage.getItem('KLARITY_HOME_TOUR_ISOPEN')) {
            setOpen(true);
        }
    }, [homeInfo]);

    const handleUpgradeClick = useCallback(() => {
        trackHeapEvent('WebApp - PPA Layout - Click - Upgrade Button', {
            showKlarityPlanUI,
            isFree,
            isKlarityStarter,
            isPaidFail,
        });
        if (showKlarityPlanUI) {
            openKlaritySubscriptionModal();
        } else {
            openSubscriptionModal();
        }
    }, [showKlarityPlanUI, isFree, isKlarityStarter, isPaidFail]);

    const handleCloseGlobalAlert = useCallback(() => {
        trackHeapEvent('WebApp - PPA Layout - Click - Close Global Alert', {
            alertType: getGloalAlert('info')?.type,
        });
        setGlobalAlert('info', { show: false, message: '', type: 'success' });
    }, []);

    const handleCreateClientClick = () => {
        setShowAddClientModal(true);
    };

    const handleScheduleAppointmentClick = () => {
        setShowAddAppointmentModal(true);
    };

    const handleCreateVisitNoteClick = () => {
        setShowCreateVisitNoteModal(true);
    };

    const handleCreateVisitNoteContinue = useCallback((result: CreateVisitNoteResult) => {
        if (result.itemType === 'STANDALONE' && result.standaloneNoteId) {
            setNonAppointmentSpecialty(result.specialty);
            setNonAppointmentClientId(result.clientId);
            setNonAppointmentNoteId(result.standaloneNoteId);
            setShowNonAppointmentUnSubmittedVisitNoteDrawer(true);
        } else if (result.appointmentId || result.textVisitUuid) {
            if (result.itemType === 'APPOINTMENT' && result.appointmentId) {
                setVisitNoteAppointmentId(result.appointmentId);
                if (result.providerTreatmentSubmitted) {
                    setShowVisitNotePreviewDrawer(true);
                } else {
                    setShowVisitNoteVideoDrawer(true);
                }
            } else if (result.itemType === 'TEXT_VISIT' && result.textVisitUuid) {
                setVisitNoteTextVisitUuid(result.textVisitUuid);
                if (result.providerTreatmentSubmitted) {
                    setShowVisitNoteViewTextDrawer(true);
                } else {
                    setShowVisitNoteTextVisitUnSubmittedDrawer(true);
                }
            }
        }
    }, []);

    const handleAddAvailabilityClick = () => {
        navigate(`${PATH.DASHBOARD}/${PATH.CALENDAR_SETTING}`);
    };

    if (loadingSubscriptionPlan === null || loadingSubscriptionPlan === true || loadingMychannels === null || loading) {
        return (
            <div className={s.spinnerWrap}>
                <Spin />
            </div>
        );
    }

    const handleTeamManagementClick = () => {
        openManageSubscriptionModal('9');
    };

    const updateBenifit: string[] = [
        'Access to more channels',
        'AI-Powered prospect management',
        'Reputation management',
        'Advanced website builder',
    ];

    const showDiscount = false;

    const providerInfo = getDisplayInfo(currentUser) || provider;

    return (
        <Layout className={s.layout}>
            <ProviderAgreement />
            <Sider theme="light" className={s.sider} collapsed={collapsed} width="255px">
                <div className={s.siderContent}>
                    <div className={`${s.menuHead} ${collapsed ? s.menuHeadCollapsed : ''}`}>
                        {collapsed && (
                            <>
                                <img src={Logo} className={s.logoS} onClick={toggleCollapsed} style={{ cursor: 'pointer' }} />
                                <Tooltip title="Expand sidebar" placement="right">
                                    <span className={s.sidebarToggle} onClick={toggleCollapsed}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M6 21C5.20435 21 4.44129 20.6839 3.87868 20.1213C3.31607 19.5587 3 18.7956 3 18V6C3 5.20435 3.31607 4.44129 3.87868 3.87868C4.44129 3.31607 5.20435 3 6 3H18C18.7956 3 19.5587 3.31607 20.1213 3.87868C20.6839 4.44129 21 5.20435 21 6V18C21 18.7956 20.6839 19.5587 20.1213 20.1213C19.5587 20.6839 18.7956 21 18 21H6ZM18 5H10V19H18C18.2652 19 18.5196 18.8946 18.7071 18.7071C18.8946 18.5196 19 18.2652 19 18V6C19 5.73478 18.8946 5.48043 18.7071 5.29289C18.5196 5.10536 18.2652 5 18 5Z" fill="#9CA3AF"/>
                                        </svg>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        {!collapsed && (
                            <>
                                <img src={Logo} className={s.logo} onClick={toggleCollapsed} style={{ cursor: 'pointer' }} />
                                <Tooltip title="Collapse sidebar">
                                    <span className={s.sidebarToggle} onClick={toggleCollapsed}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M9 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H9M9 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H9M9 4V20" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        {/* {collapsed && (
                            <img
                                onClick={() => {
                                    toggleCollapsed();
                                }}
                                src={Logo}
                                className={s.logoS}
                            />
                        )}
                        {!collapsed && (
                            <img
                                onClick={() => {
                                    toggleCollapsed();
                                }}
                                src={Logo}
                                className={s.logo}
                                style={{
                                    height: '40px',
                                    width: 'auto',
                                    marginLeft: '16px',
                                }}
                            />
                        )} */}
                    </div>
                    <div className={collapsed ? s.btnWrapCollapsed : s.btnWrap}>
                            <Popover
                                arrow={false}
                                placement={collapsed ? 'right' : 'bottomRight'}
                                content={
                                    <div className={s.popoverContent}>
                                        <div className={s.popoverItem} onClick={handleCreateClientClick}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M12.5 5.83325C12.5 7.21396 11.3807 8.33325 9.99996 8.33325C8.61925 8.33325 7.49996 7.21396 7.49996 5.83325C7.49996 4.45254 8.61925 3.33325 9.99996 3.33325C11.3807 3.33325 12.5 4.45254 12.5 5.83325Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M17.5 8.33325C17.5 9.25373 16.7538 9.99992 15.8333 9.99992C14.9128 9.99992 14.1666 9.25373 14.1666 8.33325C14.1666 7.41278 14.9128 6.66659 15.8333 6.66659C16.7538 6.66659 17.5 7.41278 17.5 8.33325Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M5.83329 8.33325C5.83329 9.25373 5.0871 9.99992 4.16663 9.99992C3.24615 9.99992 2.49996 9.25373 2.49996 8.33325C2.49996 7.41278 3.24615 6.66659 4.16663 6.66659C5.0871 6.66659 5.83329 7.41278 5.83329 8.33325Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M9.99996 10.8333C8.24564 10.8333 6.74454 11.9174 6.13016 13.4523C5.93865 13.9308 5.83329 14.4531 5.83329 14.9999V16.6666H14.1666V14.9999C14.1666 14.4531 14.0613 13.9308 13.8698 13.4523C13.2554 11.9174 11.7543 10.8333 9.99996 10.8333Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M14.1666 16.6666H18.3333V14.9999C18.3333 13.6192 17.214 12.4999 15.8333 12.4999C15.037 12.4999 14.3276 12.8723 13.8698 13.4523M14.1666 16.6666H5.83329M14.1666 16.6666V14.9999C14.1666 14.4531 14.0613 13.9308 13.8698 13.4523M5.83329 16.6666H1.66663V14.9999C1.66663 13.6192 2.78591 12.4999 4.16663 12.4999C4.96297 12.4999 5.67234 12.8723 6.13016 13.4523M5.83329 16.6666V14.9999C5.83329 14.4531 5.93865 13.9308 6.13016 13.4523M6.13016 13.4523C6.74454 11.9174 8.24564 10.8333 9.99996 10.8333C11.7543 10.8333 13.2554 11.9174 13.8698 13.4523M12.5 5.83325C12.5 7.21396 11.3807 8.33325 9.99996 8.33325C8.61925 8.33325 7.49996 7.21396 7.49996 5.83325C7.49996 4.45254 8.61925 3.33325 9.99996 3.33325C11.3807 3.33325 12.5 4.45254 12.5 5.83325ZM17.5 8.33325C17.5 9.25373 16.7538 9.99992 15.8333 9.99992C14.9128 9.99992 14.1666 9.25373 14.1666 8.33325C14.1666 7.41278 14.9128 6.66659 15.8333 6.66659C16.7538 6.66659 17.5 7.41278 17.5 8.33325ZM5.83329 8.33325C5.83329 9.25373 5.0871 9.99992 4.16663 9.99992C3.24615 9.99992 2.49996 9.25373 2.49996 8.33325C2.49996 7.41278 3.24615 6.66659 4.16663 6.66659C5.0871 6.66659 5.83329 7.41278 5.83329 8.33325Z"
                                                    stroke="#374151"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            Create a new client
                                        </div>
                                        <div className={s.popoverItem} onClick={handleScheduleAppointmentClick}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M6.66667 5.83333V2.5M13.3333 5.83333V2.5M5.83333 9.16667H14.1667M4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V5.83333C17.5 4.91286 16.7538 4.16667 15.8333 4.16667H4.16667C3.24619 4.16667 2.5 4.91286 2.5 5.83333V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5Z"
                                                    stroke="#374151"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            Schedule appointment
                                        </div>
                                        <div className={s.popoverItem} onClick={handleCreateVisitNoteClick}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M9 5H7.16667C6.24619 5 5.5 5.74619 5.5 6.66667V15.8333C5.5 16.7538 6.24619 17.5 7.16667 17.5H12.8333C13.7538 17.5 14.5 16.7538 14.5 15.8333V6.66667C14.5 5.74619 13.7538 5 12.8333 5H11M9 5C9 5.92047 9.74619 6.66667 10.6667 6.66667H9.33333C10.2538 6.66667 11 5.92047 11 5M9 5C9 4.07953 9.74619 3.33333 10.6667 3.33333H9.33333C10.2538 3.33333 11 4.07953 11 5M10 10H12.5M10 13.3333H12.5M7.5 10H7.50833M7.5 13.3333H7.50833"
                                                    stroke="#374151"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            Create visit note
                                        </div>
                                        <div className={s.popoverItem} onClick={handleAddAvailabilityClick}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M10 6.66667V10L12.5 12.5M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
                                                    stroke="#374151"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            Add availability
                                        </div>
                                        {showTeamManagement && (
                                            <div className={s.popoverItem} onClick={handleTeamManagementClick}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M10.8333 5.83333C10.8333 7.67428 9.34095 9.16667 7.5 9.16667C5.65905 9.16667 4.16667 7.67428 4.16667 5.83333C4.16667 3.99238 5.65905 2.5 7.5 2.5C9.34095 2.5 10.8333 3.99238 10.8333 5.83333Z"
                                                        fill="white"
                                                    />
                                                    <path
                                                        d="M7.5 11.6667C4.73858 11.6667 2.5 13.9052 2.5 16.6667V17.5H12.5V16.6667C12.5 13.9052 10.2614 11.6667 7.5 11.6667Z"
                                                        fill="white"
                                                    />
                                                    <path
                                                        d="M10 3.62848C10.6108 2.93646 11.5044 2.5 12.5 2.5C14.341 2.5 15.8333 3.99238 15.8333 5.83333C15.8333 7.67428 14.341 9.16667 12.5 9.16667C11.5044 9.16667 10.6108 8.7302 10 8.03819M12.5 17.5H2.5V16.6667C2.5 13.9052 4.73858 11.6667 7.5 11.6667C10.2614 11.6667 12.5 13.9052 12.5 16.6667V17.5ZM12.5 17.5H17.5V16.6667C17.5 13.9052 15.2614 11.6667 12.5 11.6667C11.5893 11.6667 10.7354 11.9102 10 12.3356M10.8333 5.83333C10.8333 7.67428 9.34095 9.16667 7.5 9.16667C5.65905 9.16667 4.16667 7.67428 4.16667 5.83333C4.16667 3.99238 5.65905 2.5 7.5 2.5C9.34095 2.5 10.8333 3.99238 10.8333 5.83333Z"
                                                        stroke="#374151"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                Add team member
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                {collapsed ? (
                                    <Tooltip title="Create" placement="right">
                                        <Button className={s.btnCollapsed} type="primary">
                                            <PlusOutlined />
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Button className={s.btn} size="large" type="primary" onClick={() => {}}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M10 3C10.5523 3 11 3.44772 11 4V9H16C16.5523 9 17 9.44772 17 10C17 10.5523 16.5523 11 16 11H11V16C11 16.5523 10.5523 17 10 17C9.44772 17 9 16.5523 9 16V11H4C3.44772 11 3 10.5523 3 10C3 9.44771 3.44772 9 4 9L9 9V4C9 3.44772 9.44772 3 10 3Z"
                                                fill="white"
                                            />
                                        </svg>
                                        Create
                                    </Button>
                                )}
                            </Popover>
                        </div>
                    <Menu
                        onClick={onClickMenu}
                        key={currentKey}
                        selectedKeys={[currentKey]}
                        mode="inline"
                        items={items}
                    />
                </div>
                <div className={s.siderFooter}>
                    <div className={s.footerMenu}>
                        {/* ppa 不展示upgrade按钮 */}
                        {/* {!isPPAProvider && (isFree || isKlarityStarter) &&
                            !isPaidFail &&
                            !collapsed &&
                            currentPlan &&
                            (
                                <div
                                    className={`${s.upgradeWrap} ${isOpenUpgrade ? s.upgradeWrapOpen : ''}`}
                                >
                                    <div className={s.uTitle}>
                                        <div className={s.uLeft}>
                                            Upgrade now
                                            {showDiscount && (
                                                <span>
                                                    {' '}
                                                    to elevate your practice
                                                    with our early bird offer;
                                                    save
                                                    <span
                                                        className={s.highLight}
                                                    >
                                                        {' '}
                                                        20%
                                                    </span>
                                                    !
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            onClick={() => {
                                                trackHeapEvent('WebApp - PPA Layout - Click - Toggle Upgrade Details', {
                                                    isOpenUpgrade,
                                                });
                                                setIsOpenUpgrade(
                                                    !isOpenUpgrade,
                                                );
                                            }}
                                            className={s.uRight}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M5 15L12 8L19 15"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    </div>

                                    <div className={s.uItemWrap}>
                                        {updateBenifit.map((val: string) => {
                                            return (
                                                <div
                                                    className={s.uItem}
                                                    key={val}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="10"
                                                        viewBox="0 0 14 10"
                                                        fill="none"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            clipRule="evenodd"
                                                            d="M13.7071 0.292893C14.0976 0.683417 14.0976 1.31658 13.7071 1.70711L5.70711 9.70711C5.31658 10.0976 4.68342 10.0976 4.29289 9.70711L0.292893 5.70711C-0.0976311 5.31658 -0.0976311 4.68342 0.292893 4.29289C0.683417 3.90237 1.31658 3.90237 1.70711 4.29289L5 7.58579L12.2929 0.292893C12.6834 -0.0976311 13.3166 -0.0976311 13.7071 0.292893Z"
                                                            fill="#0E62F4"
                                                        />
                                                    </svg>
                                                    {val}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <Button
                                        block
                                        // type="primary"
                                        className={s.upgrade}
                                        onClick={handleUpgradeClick}
                                    >
                                        🚀 Upgrade
                                    </Button>
                                </div>
                            )} */}
                        <div className={`${s.menuItem} ${s.withoutBottomGap} ${collapsed ? s.menuItemCollapsed : ''}`}>
                            <div
                                className={s.needHelp}
                                onClick={() => {
                                    trackHeapEvent('WebApp - PPA Layout - Click - Need Help', {
                                        isKlarity,
                                    });
                                    if (isKlarity) {
                                        setHelpCenterActiveKey('2');
                                    }
                                    navigate(`${PATH.DASHBOARD}/${PATH.HELP_CENTER}`);
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M6.22766 7C6.77678 5.83481 8.25843 5 10.0001 5C12.2092 5 14.0001 6.34315 14.0001 8C14.0001 9.39944 12.7224 10.5751 10.9943 10.9066C10.4519 11.0106 10.0001 11.4477 10.0001 12M10 15H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                                        stroke="#0E62F4"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {!collapsed && <span>Need help?</span>}
                            </div>
                        </div>
                        <div className={`${s.menuItem} ${collapsed ? s.menuItemCollapsed : ''}`}>
                            {!collapsed && (
                                <>
                                    <ProfilePopover
                                        provider={providerInfo}
                                        userType="klarity"
                                        photoClassName={s.profilePhoto}
                                        isShowSwitchAccount={isShowSwitchAccount && !isOpsPreview}
                                    />
                                    <div className={s.profileName}>
                                        {providerInfo.firstName || providerInfo?.email}
                                        <span className={s.adminBadge}>Admin</span>
                                    </div>
                                </>
                            )}
                            <div ref={dropRef}>
                                <Dropdown
                                    className={s.dropDown}
                                    menu={{ items: ProviderMenus }}
                                    trigger={['click']}
                                    open={dropdownOpen}
                                    onOpenChange={setDropdownOpen}
                                >
                                    <div className={s.moreActions} />
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </Sider>
            <Layout className={s.siteLayout} id="sitePPALayout">
                <Spin spinning={loadingSubscriptionPlan || loadingMychannels || loadingReviewList}>
                    {getGloalAlert('info')?.show && (
                        <Alert
                            className={s.globalAlert}
                            message={getGloalAlert('info')?.message || ''}
                            type={getGloalAlert('info')?.type || 'success'}
                            closable
                            onClose={handleCloseGlobalAlert}
                        />
                    )}
                    <Content>
                        <Outlet />
                    </Content>
                </Spin>
            </Layout>

            <Tour
                rootClassName={s.tour}
                open={shouldShowKlarityTour}
                onClose={() => {
                    trackHeapEvent('WebApp - PPA Layout - Click - Close Tour', {
                        currentStep,
                    });
                    setOpen(false);
                    setDropdownOpen(false);
                    localStorage.setItem('KLARITY_HOME_TOUR_ISOPEN', 'true');
                }}
                onChange={(current) => {
                    trackHeapEvent('WebApp - PPA Layout - View - Tour Step Change', {
                        fromStep: currentStep,
                        toStep: current,
                    });
                    setCurrentStep(current);
                    if (current === steps.length - 1) {
                        setDropdownOpen(true);
                    } else {
                        setDropdownOpen(false);
                    }
                }}
                steps={steps}
                indicatorsRender={(current, total) => (
                    <span>
                        Step {current + 1} out of {total}
                    </span>
                )}
            />
            <AddAndUpdateProspects
                show={showAddClientModal}
                onCannel={() => {
                    setShowAddClientModal(false);
                }}
                onSuccess={() => {
                    setShowAddClientModal(false);
                }}
            />
            <PracticeSwitcher open={showPracticeSwitcher} onClose={() => setShowPracticeSwitcher(false)} />
            {showAddAppointmentModal && (
                <AddAndEditCalendarEventModal
                    onCancel={() => {
                        setShowAddAppointmentModal(false);
                    }}
                    onSuccess={() => {
                        setShowAddAppointmentModal(false);
                    }}
                    allSpecialityData={allSpeciality}
                />
            )}
            <CreateVisitNoteModal
                open={showCreateVisitNoteModal}
                onClose={() => setShowCreateVisitNoteModal(false)}
                onContinue={handleCreateVisitNoteContinue}
            />
            {showVisitNoteVideoDrawer && (
                <VideoVisitUnSubmittedDrawer
                    show={showVisitNoteVideoDrawer}
                    onClose={() => {
                        setShowVisitNoteVideoDrawer(false);
                        setVisitNoteAppointmentId(undefined);
                    }}
                    appointmentId={visitNoteAppointmentId}
                />
            )}
            {showVisitNotePreviewDrawer && (
                <VideoVisitSubmittedDrawer
                    show={showVisitNotePreviewDrawer}
                    onClose={() => {
                        setShowVisitNotePreviewDrawer(false);
                        setVisitNoteAppointmentId(undefined);
                    }}
                    appointmentId={visitNoteAppointmentId}
                />
            )}
            {showVisitNoteTextVisitUnSubmittedDrawer && visitNoteTextVisitUuid && (
                <TextVisitUnSubmittedDrawer
                    show={showVisitNoteTextVisitUnSubmittedDrawer}
                    fullyWithSaveFun
                    onClose={() => {
                        setShowVisitNoteTextVisitUnSubmittedDrawer(false);
                        setVisitNoteTextVisitUuid(undefined);
                    }}
                    textVisitUuid={visitNoteTextVisitUuid}
                />
            )}
            {showVisitNoteViewTextDrawer && visitNoteTextVisitUuid && (
                <TextVisitSubmittedDrawer
                    open={showVisitNoteViewTextDrawer}
                    onClose={() => {
                        setShowVisitNoteViewTextDrawer(false);
                        setVisitNoteTextVisitUuid(undefined);
                    }}
                    textVisitUuid={visitNoteTextVisitUuid}
                />
            )}
            {showNonAppointmentUnSubmittedVisitNoteDrawer && (
                <NonAppointmentUnSubmittedVisitNoteDrawer
                    show={showNonAppointmentUnSubmittedVisitNoteDrawer}
                    onClose={() => {
                        setShowNonAppointmentUnSubmittedVisitNoteDrawer(false);
                        setNonAppointmentSpecialty(undefined);
                        setNonAppointmentClientId(undefined);
                        setNonAppointmentNoteId(undefined);
                    }}
                    clientId={nonAppointmentClientId}
                    specialty={nonAppointmentSpecialty}
                    noteId={nonAppointmentNoteId}
                />
            )}
        </Layout>
    );
};

export default PPALayout;
